import { Component, forwardRef, inject, Input, OnChanges, OnInit, Provider, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isEqual, isNil } from 'lodash';
import { FormsUtil, ModalService } from 'mbs-ui-kit';
import { ErrorCode } from 'mbs-ui-shared@constants/errorCodes';
import { combineLatest, filter, first, of, Subscription, switchMap } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ForceValidType } from 'wizards/models/base/other-base-models/force-valid-type';
import { DestinationVMStepValue, DestinationVMStepValueForm } from 'wizards/models/destination-vm-models';
import { SelectHostStepValue } from 'wizards/models/select-host-models';
import { VirtualMachinesType } from 'wizards/models/select-virtual-machines-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { ParamsForGetVirtualMachinesOrDisksList, RestoreSourceData, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { DestinationTableData } from 'wizards/steps/components/destination-table/destination-table.component';
import { BaseForDestinationStep } from 'wizards/steps/only-restore-steps/base/base-for-destination-step';

import { FormGroupComponent, RadioComponent, RadioDescription, SelectComponent, SwitcherComponent } from 'mbs-ui-kit';

import { DestinationTableComponent } from 'wizards/steps/components/destination-table/destination-table.component';

import { DestinationFolderInputComponent } from 'mbs-ui-shared@components/destination-folder-input/destination-folder-input.component';

import { CommonModule } from '@angular/common';

import { FormsModule, ReactiveFormsModule } from '@angular/forms';

const DestinationVMStepValueAccessor: Provider = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => DestinationVMStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-destination-vm-step',
  templateUrl: './destination-vm-step.component.html',
  providers: [DestinationVMStepValueAccessor],
  imports: [
    CommonModule,
    DestinationFolderInputComponent,
    DestinationTableComponent,
    FormGroupComponent,
    FormsModule,
    I18NextPipe,
    RadioComponent,
    RadioDescription,
    ReactiveFormsModule,
    SelectComponent,
    SwitcherComponent
  ]
})
export class DestinationVMStepComponent extends BaseForDestinationStep<DestinationVMStepValue> implements OnInit, OnChanges {
  i18nPipe = inject(I18NextPipe);
  mainService: RemoteManagementWizardsService;
  modalService = inject(ModalService);
  stepService = inject(WizardStepsService);

  @Input() importVirtualMachine: boolean;
  @Input() needShowComputers: boolean;
  @Input() canLoadMachines: boolean;

  public readonly elementsSelector = {
    name: {
      notVirtualOrHyperVBlock: 'notVirtualOrHyperVBlock',
      notVirtualHyperVAndImportBlock: 'notVirtualHyperVAndImportBlock',
      notVirtualAndHyperVOrShowBlock: 'notVirtualAndHyperVOrShowBlock',
      tableGrid: 'tableGrid'
    }
  };
  private readonly paramsForMachines: ParamsForGetVirtualMachinesOrDisksList = {
    agentType: 'backup',
    commandType: 'GetVirtualMachineList',
    params: {
      PlanId: this.mainService.planId,
      Type: this.isRestoreVMWare ? 'VMWare' : this.isRestoreProxmox ? 'Proxmox' : 'HyperV',
      Server: '',
      Login: '',
      Password: '',
      IsCluster: false,
      Order: 'DisplayNameAsc'
    }
  };

  public loadingTableData = false;
  public itemsData: { data: RestoreSourceData; items: unknown[] } = { data: null, items: [] };

  public dataStoreList: string[] = [];
  public dataStoreLoading = false;

  private previousHostsValue: Partial<SelectHostStepValue> = null;
  private treeSubscribe$: Subscription;
  private firstLoad = true;

  get destinationFolderControl(): FormControl<string> {
    return this.stepForm.get('destinationFolder') as FormControl<string>;
  }

  get notVirtualAndHyperV(): boolean {
    return !this.isVirtual && this.isRestoreHyperV;
  }

  get needTableShow(): boolean {
    return !this.isVirtual && (this.importVirtualMachine || this.needShowComputers) && !this.value?.toOriginalLocation;
  }

  constructor() {
    const mainService = inject(RemoteManagementWizardsService);

    super();

    this.mainService = mainService;
  }

  ngOnInit(): void {
    this.initForm();
    this.setValues();
    this.destinationFolderParams = this.getParamsForDestinationFolder();

    this.toggleFormControls(['dataStore'], this.isRestoreVMWareToVSphere || this.isRestoreProxmox);

    this.updateDataForTableFromPlanType();
  }

  initForm(): void {
    this.stepForm = new FormGroup<DestinationVMStepValueForm>(
      {
        dataStore: new FormControl('', [Validators.required]),
        destinationFolder: new FormControl(''),
        overwriteExistingVM: new FormControl(false),
        powerOnVM: new FormControl(false),
        toOriginalLocation: new FormControl(false),
        specificList: new FormControl([])
      },
      this.destinationVMFormValidator.bind(this)
    );

    this.initFormEvents();
  }

  destinationVMFormValidator(): ValidationErrors | null {
    if (!this.needTableShow) return null;

    return this.stepService.hyperVNotFoundError$.value
      ? { hyperVNotFound: true }
      : !this.overwriteAndSpecific?.valid || !this.itemsData?.data?.folders?.length
        ? { specificError: true }
        : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.stepForm) return;

    if (this.isRestoreVMWare || this.isRestoreProxmox) {
      if (this.isEdit && this.firstLoad) {
        queueMicrotask(() => this.toggleFormControls(['dataStore'], this.isRestoreVMWareToVSphere || this.isRestoreProxmox));
      } else this.toggleFormControls(['dataStore'], this.isRestoreVMWareToVSphere || this.isRestoreProxmox);
    }

    if (changes.canLoadMachines?.currentValue && this.needTableShow) {
      this.stepService.treeData$.next(this.stepService.treeData$.value);
    }

    if (changes.isVirtual || changes.importVirtualMachine || changes.needShowComputers) {
      this.switchSpecificListStateAndForceSubscribeIfNeed();

      if (this.isRestoreVMWare || this.isRestoreProxmox) {
        queueMicrotask(() => this.toggleFormControls(['destinationFolder'], !this.needTableShow));
      } else {
        queueMicrotask(() =>
          this.powerOnVMChangeHandler(!changes.isVirtual?.currentValue && this.value.toOriginalLocation && this.importVirtualMachine)
        );
      }
    }
  }

  forceValid(data: ForceValidType = null): void {
    if (this.needTableShow && !this.overwriteAndSpecific?.valid) {
      this.forceValidate = true;
    }

    if (this.needTableShow && this.stepService.hyperVNotFoundError$?.value && (data.isNext || data.isSave)) {
      this.stepService.openErrorModal(this.stepService.hyperVNotFoundError$?.value);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  switchSpecificListStateAndForceSubscribeIfNeed(): void {
    if (this.isEdit) {
      queueMicrotask(() => {
        this.toggleFormControls(['specificList'], this.needTableShow);
        if (this.needTableShow) this.stepService.treeData$.next({ ...this.stepService.treeData$.value });
      });
    } else this.toggleFormControls(['specificList'], this.needTableShow);
  }

  updateForm(value: DestinationVMStepValue): void {
    if (!value) return;

    this.stepForm.reset(value);

    if (this.isVMRestore || this.isRestoreProxmox) {
      this.powerOnVMChangeHandler(value.toOriginalLocation && (this.isRestoreVMWare || this.isRestoreProxmox || this.importVirtualMachine));
    }

    if (this.needTableShow) {
      this.overwriteAndSpecific = { overwriteExisting: value.overwriteExistingVM, specificList: value.specificList, valid: true };
    }
  }

  updateDataForTableFromPlanType(): void {
    if (!this.isRestoreVMWare && !this.isRestoreProxmox) return this.getDataForTable();

    this.stepService
      .pipeProperty('hosts')
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (result: Partial<SelectHostStepValue>) => {
          const allFields = result?.host && result.server && result.login && result.password;

          if (allFields && (!this.previousHostsValue || !isEqual(this.previousHostsValue, result))) {
            this.previousHostsValue = result;
            this.getDataForTable();

            if (!this.isRestoreVMWareToVSphere && !this.isRestoreProxmox) return;

            if (this.dataStoreList?.length && this.stepForm?.get('dataStore')?.value && (this.isCreate || !this.firstLoad)) {
              this.stepForm?.get('dataStore').reset();
            }
            this.getDataStoreList(result);
          }
        }
      });
  }

  getDataForTable(): void {
    if (this.treeSubscribe$) this.treeSubscribe$.unsubscribe();

    this.treeSubscribe$ = this.stepService.treeData$
      .pipe(
        filter(() => !this.stepService.hyperVNotFoundError$.value),
        filter(() => (this.isCreate ? this.canLoadMachines : this.canLoadMachines || this.firstLoad)),
        filter((data: RestoreSourceData) => {
          return this.needTableShow && !!data?.folders && this.stepForm?.get('specificList').enabled;
        }),
        tap(() => (this.loadingTableData = true)),
        switchMap((data: RestoreSourceData) =>
          combineLatest([
            of(data),
            this.itemsData?.items?.length
              ? of({ data: { items: this.itemsData?.items } })
              : this.stepService
                  .getRemoteCommandData(
                    this.getParamsForMachinesRequest(),
                    this.mainService.targetComputerData?.hid || this.mainService.hid
                  )
                  .pipe(
                    catchError((e) => {
                      if (e?.error?.errorCode === ErrorCode.HyperVNotFound) {
                        this.stepService.hyperVNotFoundError$.next({ title: e.error?.title, content: e.error?.detail });
                        this.stepForm.updateValueAndValidity();

                        return of([]);
                      }

                      return of(e);
                    })
                  )
          ])
        ),
        untilDestroyed(this)
      )
      .subscribe({
        next: ([data, result]) => {
          this.itemsData = { data: data || { folders: [], treeData: [] }, items: result?.data?.items || [] };
          this.loadingTableData = false;
          this.firstLoad = false;

          if (this.isRestoreVMWare || this.isRestoreProxmox) this.toggleFormControls(['destinationFolder'], !this.needTableShow);
        },
        error: () => {
          this.firstLoad = false;
          this.loadingTableData = false;
        }
      });
  }

  getDataStoreList(value: Partial<SelectHostStepValue>): void {
    this.dataStoreLoading = true;
    this.loadInfo.emit({ loading: true });

    const params = {
      agentType: 'backup',
      commandType: 'GetVirtualMachineDataStoreList',
      params: {
        PlanId: this.mainService.planId,
        Type: this.isRestoreProxmox ? VirtualMachinesType.Proxmox : VirtualMachinesType.VMWare,
        Server: value.server,
        Login: value.login,
        Password: value.password,
        IsCluster: false,
        Offset: null,
        Limit: null,
        Order: 'DisplayNameAsc',
        Search: null,
        Host: value.host
      }
    };

    this.stepService
      .getRemoteCommandData(params, this.mainService.targetComputerData?.hid || this.mainService.hid)
      .pipe(first(), untilDestroyed(this))
      .subscribe({
        next: (result: { data: { items: { name: string }[] } }) => {
          this.dataStoreList = result?.data?.items?.map((h) => h.name) || [];

          if (!this.value.dataStore && this.dataStoreList.length) {
            this.stepForm.get('dataStore').setValue(this.dataStoreList[0]);
          }
          this.loadInfo.emit({ loading: false });
          this.dataStoreLoading = false;
        },
        error: () => {
          this.loadInfo.emit({ loading: false });
          this.dataStoreLoading = false;
        }
      });
  }

  powerOnVMChangeHandler(state: boolean): void {
    if (!isNil((state as unknown as { target?: { value: unknown } })?.target?.value)) return;

    this.toggleFormControls(['destinationFolder'], !state);

    if (this.isRestoreHyperV && this.needTableShow && (!this.itemsData.data || !this.itemsData?.items?.length)) {
      this.getDataForTable();
    }
  }

  overwriteAndSpecificChangeHandler(tableData: DestinationTableData): void {
    if (!this.stepForm) return;

    queueMicrotask(() => {
      this.stepForm.get('overwriteExistingVM').setValue(tableData.overwriteExisting);
      this.stepForm.get('specificList').setValue(tableData.specificList);
      this.forceValidate = false;
    });
  }

  private getParamsForMachinesRequest(): ParamsForGetVirtualMachinesOrDisksList {
    if (this.isRestoreVMWare || this.isRestoreProxmox) {
      this.paramsForMachines.params.Password = this.previousHostsValue.password;
      this.paramsForMachines.params.Login = this.previousHostsValue.login;
      this.paramsForMachines.params.Server = this.previousHostsValue.server;
    }

    return this.paramsForMachines;
  }
}
